// src/UpdateProfile.tsx
//
// Úprava profilu (/updateprofile) – tři samostatné části s vlastním odesláním:
//  1) osobní údaje a zbrojní průkaz (formulář se sticky lištou Uložit; rok
//     narození a pohlaví jen ke čtení – mění je správce, rozhodnutí majitele
//     12. 10. 2026),
//  2) změna hesla (ověření současným heslem),
//  3) smazání účtu (dialog s heslem a opsáním slova SMAZAT; zruší i přihlášky
//     na nadcházející závody a zápisy na čekacích listinách; deleteAccount
//     používá i AuthPage k dokončení nedokončeného smazání).
// Titulek a Zpět (/profile) dodává Screen v App.tsx, nepřihlášené odchytí
// RequireAuth. Přehled údajů (ProfileCard) je jen na stránce Profil – tady
// formulář začíná nahoře.

import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
  type User,
} from "firebase/auth";
import {
  runTransaction,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { clearFirestoreCache, db, restartApp } from "./firebase";
import { useToast } from "./ui/Toast";
import { Modal } from "./ui/ConfirmDialog";
import { EmptyState, ErrorState, LoadingState } from "./ui/AsyncState";
import { Section } from "./ui/Section";
import { Field } from "./ui/Field";
import { PhoneInput, normalizePhone, validatePhone } from "./ui/PhoneInput";
import { scrollIntoMain } from "./ui/AppShell";
import { useCurrentUser } from "./utils/useCurrentUser";
import {
  firebaseErrorMessage,
  firebaseErrorMessages,
} from "./utils/firebaseErrors";
import { reportError, reportWarning } from "./utils/reportError";
import { toTitleCase } from "./utils/nameUtils";
import { CONTACT_EMAIL } from "./utils/contact";
import { startOfDay } from "./utils/dates";
import { deleteRegistrationCounted, RaceBusyError } from "./utils/raceStats";
import { startListPath } from "./utils/startList";
import { leaveWaitlist, loadMyWaitlist } from "./utils/waitlist";
import {
  DELETED_PROFILES_COLLECTION,
  PENDING_PROFILES_COLLECTION,
  usableGender,
} from "./utils/completeRegistration";

// ─────────────────────────────────────────────────────────────
//  Pravidla a konstanty
// ─────────────────────────────────────────────────────────────

/** Jméno a příjmení: písmena vč. diakritiky, mezera, pomlčka, aspoň 2 znaky (beze změny). */
const NAME_PATTERN = /^[A-Za-zÁ-Žá-ž\s-]{2,}$/;

/** Heslo: aspoň 6 znaků a aspoň jedna číslice (beze změny). */
const PASSWORD_PATTERN = /^(?=.*\d).{6,}$/;

/** Slovo, které se musí opsat před smazáním účtu (velikost písmen nerozhoduje). */
const DELETE_CONFIRM_WORD = "SMAZAT";

/** Kódy Firebase Auth pro špatné heslo (liší se podle verze a ochrany proti zjišťování e-mailů). */
const WRONG_PASSWORD_CODES = [
  "auth/wrong-password",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
];

const PERSONAL_SECTION_ID = "profile-personal";
const PASSWORD_SECTION_ID = "profile-password";
const DELETE_FORM_ID = "profile-delete-form";

// ─────────────────────────────────────────────────────────────
//  Pomocné funkce
// ─────────────────────────────────────────────────────────────

/** Hodnota pole dokumentu pro textový input: číslo převede na text, jiné typy = "". */
const textOf = (raw: unknown): string => {
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
};

const pad2 = (n: number | string): string => String(n).padStart(2, "0");

/**
 * Platnost ZP pro <input type="date"> ("YYYY-MM-DD" – tak ji formulář ukládá).
 * Starší záznamy z importu mohou mít "31. 12. 2027", ISO čas nebo Timestamp:
 * ty se převedou jen pro zobrazení, uloží se až po změně pole. Nerozpoznaný
 * tvar = "" (pole je prázdné, uložená hodnota zůstane netknutá).
 */
const toDateInputValue = (raw: unknown): string => {
  if (typeof raw === "string") {
    const value = raw.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const cz = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(value);
    if (cz) return `${cz[3]}-${pad2(cz[2])}-${pad2(cz[1])}`;
    return "";
  }
  let date: Date | null = null;
  if (raw instanceof Date) {
    date = raw;
  } else if (
    raw &&
    typeof raw === "object" &&
    typeof (raw as { toDate?: unknown }).toDate === "function"
  ) {
    date = (raw as { toDate: () => Date }).toDate();
  }
  if (!date || isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/** Kód chyby Firebase ("auth/…", "permission-denied"…), jinak "". */
const errorCode = (err: unknown): string => {
  const code =
    err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  return typeof code === "string" ? code : "";
};

/**
 * Chyba ověření heslem, která patří přímo k poli s heslem (špatné heslo,
 * zablokování po mnoha pokusech). Ostatní chyby (síť…) vrací undefined.
 */
const passwordFieldError = (err: unknown): string | undefined => {
  const code = errorCode(err);
  if (WRONG_PASSWORD_CODES.indexOf(code) !== -1) {
    return firebaseErrorMessages["auth/wrong-password"];
  }
  if (code === "auth/too-many-requests") return firebaseErrorMessage(err);
  return undefined;
};

/** Ověření současným heslem – Firebase ho vyžaduje před změnou hesla i smazáním účtu. */
const reauthenticate = (user: User, password: string) =>
  reauthenticateWithCredential(
    user,
    EmailAuthProvider.credential(user.email ?? "", password)
  );

/**
 * Chyba ve formuláři: sekci odscroluje pod fixní hlavičku (scroll kontejner
 * je .lks-main, ne okno) a fokus dá chybnému poli bez dalšího skoku.
 */
const focusField = (sectionId: string, fieldId: string): void => {
  scrollIntoMain(document.getElementById(sectionId));
  const field = document.getElementById(fieldId);
  if (field) field.focus({ preventScroll: true });
};

// ─────────────────────────────────────────────────────────────
//  1) Osobní údaje a zbrojní průkaz
// ─────────────────────────────────────────────────────────────

/** Hodnoty formuláře; klíče = pole dokumentu users (nemění se). */
interface ProfileFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  zpNumber: string;
  zpValidity: string;
}

type ProfileErrorKey = "firstName" | "lastName" | "phone";
type ProfileErrors = Partial<Record<ProfileErrorKey, string>>;

/** Pořadí polí pro fokus na první chybu. */
const PROFILE_ERROR_ORDER: ProfileErrorKey[] = ["firstName", "lastName", "phone"];

const PROFILE_FIELD_IDS: Record<ProfileErrorKey, string> = {
  firstName: "profileFirstName",
  lastName: "profileLastName",
  phone: "profilePhone",
};

/** Poznámka u roku narození a pohlaví (jen ke čtení). */
const LOCKED_NOTE_ID = "profileLockedNote";

/** Rok narození k zobrazení (číslo i text z importu), jinak „Neuvedeno“. */
const birthYearText = (raw: unknown): string => textOf(raw).trim() || "Neuvedeno";

/** Pohlaví k zobrazení: „Muž“ / „Žena“ (i „MUŽ“ z importu), jiný text beze změny, jinak „Neuvedeno“. */
const genderText = (raw: unknown): string => {
  const gender = usableGender(raw);
  if (gender === "muž") return "Muž";
  if (gender === "žena") return "Žena";
  return textOf(raw).trim() || "Neuvedeno";
};

const formFromDoc = (userDoc: DocumentData): ProfileFormValues => ({
  firstName: textOf(userDoc.firstName),
  lastName: textOf(userDoc.lastName),
  phone: textOf(userDoc.phone),
  zpNumber: textOf(userDoc.zpNumber),
  zpValidity: toDateInputValue(userDoc.zpValidity),
});

const validateName = (
  value: string,
  emptyMessage: string,
  invalidMessage: string
): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return emptyMessage;
  return NAME_PATTERN.test(trimmed) ? undefined : invalidMessage;
};

const validateProfile = (values: ProfileFormValues): ProfileErrors => {
  const errors: ProfileErrors = {};
  const firstName = validateName(
    values.firstName,
    "Zadejte jméno.",
    "Jméno může obsahovat jen písmena, mezery a pomlčky (alespoň 2 znaky)."
  );
  if (firstName) errors.firstName = firstName;
  const lastName = validateName(
    values.lastName,
    "Zadejte příjmení.",
    "Příjmení může obsahovat jen písmena, mezery a pomlčky (alespoň 2 znaky)."
  );
  if (lastName) errors.lastName = lastName;
  // Telefon je povinný jako dosud; validuje se vždy, takže uživatel uvidí
  // i chybu staršího uloženého čísla (např. chybějící předvolbu).
  const phone = validatePhone(values.phone, true);
  if (phone) errors.phone = phone;
  return errors;
};

/**
 * Pole k uložení – jen ta, která uživatel změnil (oproti hodnotám při
 * načtení formuláře). Nezměněná pole se nepřepisují, takže starší tvar
 * dat z importu (velká písmena, Timestamp platnosti ZP…) zůstane, dokud
 * ho uživatel sám neupraví.
 */
const changedFields = (
  initial: ProfileFormValues,
  values: ProfileFormValues
): Record<string, string> => {
  const changes: Record<string, string> = {};
  if (values.firstName.trim() !== initial.firstName.trim()) {
    changes.firstName = toTitleCase(values.firstName);
  }
  if (values.lastName.trim() !== initial.lastName.trim()) {
    changes.lastName = toTitleCase(values.lastName);
  }
  if (normalizePhone(values.phone) !== normalizePhone(initial.phone)) {
    changes.phone = normalizePhone(values.phone);
  }
  if (values.zpNumber.trim() !== initial.zpNumber.trim()) {
    changes.zpNumber = values.zpNumber.trim();
  }
  if (values.zpValidity !== initial.zpValidity) {
    changes.zpValidity = values.zpValidity;
  }
  return changes;
};

interface ProfileFormProps {
  user: User;
  userDoc: DocumentData;
  /** Rozepsaná (neuložená) změna hesla v sekci Změna hesla. */
  passwordDirty?: React.MutableRefObject<boolean>;
}

/** Formulář osobních údajů; výchozí hodnoty se berou z dokumentu jen při mountu. */
const ProfileForm: React.FC<ProfileFormProps> = ({
  user,
  userDoc,
  passwordDirty,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [initial] = useState<ProfileFormValues>(() => formFromDoc(userDoc));
  const [form, setForm] = useState<ProfileFormValues>(initial);
  const [errors, setErrors] = useState<ProfileErrors>({});
  const [saving, setSaving] = useState(false);

  const setField = (key: keyof ProfileFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === "firstName" || key === "lastName" || key === "phone") {
      setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const nextErrors = validateProfile(form);
    setErrors(nextErrors);
    const firstInvalid = PROFILE_ERROR_ORDER.find((key) => nextErrors[key]);
    if (firstInvalid) {
      focusField(PERSONAL_SECTION_ID, PROFILE_FIELD_IDS[firstInvalid]);
      return;
    }

    const changes = changedFields(initial, form);
    if (passwordDirty?.current) {
      // Heslo má vlastní tlačítko – jinak by rozepsané heslo tiše zmizelo
      // s odchodem na /profile po uložení.
      showToast(
        "Rozepsanou změnu hesla nejdřív dokončete tlačítkem „Změnit heslo“ (nebo pole vymažte).",
        "warning"
      );
      return;
    }
    if (Object.keys(changes).length === 0) {
      showToast("Nic jste nezměnili.", "info");
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid), changes);
    } catch (err) {
      reportError("UpdateProfile.saveProfile", err, {
        uid: user.uid,
        fields: Object.keys(changes),
      });
      showToast(firebaseErrorMessage(err, "Údaje se nepodařilo uložit."), "error");
      setSaving(false);
      return;
    }

    // Jméno v profilu Firebase Auth (displayName) – dřív se obnovovalo jen
    // při změně příjmení. Je to jen kopie pro zobrazení: na odpověď se
    // nečeká (pomalá síť by držela spinner) a selhání uložení nezastaví.
    if (changes.firstName !== undefined || changes.lastName !== undefined) {
      updateProfile(user, {
        displayName: `${toTitleCase(form.firstName)} ${toTitleCase(form.lastName)}`,
      }).catch((err: unknown) => {
        reportWarning("UpdateProfile.displayName", err, { uid: user.uid });
      });
    }

    // Toast žije v provideru a přežije navigaci – žádné umělé čekání.
    showToast("Údaje byly uloženy.", "success");
    navigate("/profile");
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Section id={PERSONAL_SECTION_ID} title="Osobní údaje" icon="bi-person">
        <Field
          id={PROFILE_FIELD_IDS.firstName}
          label="Jméno"
          required
          error={errors.firstName}
        >
          <input
            type="text"
            className="form-control"
            autoComplete="given-name"
            autoCapitalize="words"
            value={form.firstName}
            onChange={(e) => setField("firstName", e.target.value)}
          />
        </Field>

        <Field
          id={PROFILE_FIELD_IDS.lastName}
          label="Příjmení"
          required
          error={errors.lastName}
        >
          <input
            type="text"
            className="form-control"
            autoComplete="family-name"
            autoCapitalize="words"
            value={form.lastName}
            onChange={(e) => setField("lastName", e.target.value)}
          />
        </Field>

        {/* Jen ke čtení: rok narození a pohlaví (cena přihlášky, kategorie)
            mění jen správce – pravidla vlastníkovi zápis odmítnou
            (ownerEditableUserKeys); do changedFields nepatří. */}
        <div className="row">
          <div className="col-sm-6">
            <Field id="profileBirthYear" label="Rok narození">
              <input
                type="text"
                className="form-control bg-light"
                value={birthYearText(userDoc.birthYear)}
                readOnly
                aria-describedby={LOCKED_NOTE_ID}
              />
            </Field>
          </div>
          <div className="col-sm-6">
            <Field id="profileGender" label="Pohlaví">
              <input
                type="text"
                className="form-control bg-light"
                value={genderText(userDoc.gender)}
                readOnly
                aria-describedby={LOCKED_NOTE_ID}
              />
            </Field>
          </div>
        </div>
        <p
          id={LOCKED_NOTE_ID}
          className="form-text mb-3"
          style={{ marginTop: "calc(-1 * var(--lks-sp-2, 0.5rem))" }}
        >
          Pohlaví a rok narození může změnit jen správce. Pokud nesedí, napište na{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <PhoneInput
          id={PROFILE_FIELD_IDS.phone}
          value={form.phone}
          onChange={(full) => setField("phone", full)}
          required
          error={errors.phone}
          className="mb-0"
        />
      </Section>

      <Section title="Zbrojní průkaz" icon="bi-card-text">
        <Field id="profileZpNumber" label="Číslo ZP">
          <input
            type="text"
            className="form-control"
            autoComplete="off"
            value={form.zpNumber}
            onChange={(e) => setField("zpNumber", e.target.value)}
          />
        </Field>

        <Field id="profileZpValidity" label="Platnost ZP" className="mb-0">
          <Field.Date
            value={form.zpValidity}
            onValueChange={(value) => setField("zpValidity", value)}
            clearable
            clearLabel="Vymazat platnost ZP"
          />
        </Field>
      </Section>

      {/* Přímý potomek <form>: sticky lišta nesmí být uvnitř Section */}
      <div className="lks-form-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate("/profile")}
          disabled={saving}
        >
          Zrušit
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (
            <span
              className="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            />
          ) : (
            <i className="bi bi-floppy me-2" aria-hidden="true" />
          )}
          Uložit změny
        </button>
      </div>
    </form>
  );
};

// ─────────────────────────────────────────────────────────────
//  2) Změna hesla
// ─────────────────────────────────────────────────────────────

type PasswordErrorKey = "currentPassword" | "newPassword" | "confirmPassword";
type PasswordErrors = Partial<Record<PasswordErrorKey, string>>;

const PASSWORD_ERROR_ORDER: PasswordErrorKey[] = [
  "currentPassword",
  "newPassword",
  "confirmPassword",
];

const PASSWORD_FIELD_IDS: Record<PasswordErrorKey, string> = {
  currentPassword: "pwCurrent",
  newPassword: "pwNew",
  confirmPassword: "pwConfirm",
};

/** Samostatný formulář změny hesla (dřív smíchaný s úpravou profilu). */
const PasswordForm: React.FC<{
  user: User;
  /** Hlásí rodiči, že je v polích rozepsané heslo (ProfileForm ho neuloží omylem). */
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ user, onDirtyChange }) => {
  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(!!(currentPassword || newPassword || confirmPassword));
    }
  }, [currentPassword, newPassword, confirmPassword, onDirtyChange]);
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [saving, setSaving] = useState(false);

  const clearError = (key: PasswordErrorKey) =>
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const showFieldError = (key: PasswordErrorKey, message: string) => {
    setErrors({ [key]: message });
    focusField(PASSWORD_SECTION_ID, PASSWORD_FIELD_IDS[key]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const nextErrors: PasswordErrors = {};
    if (!currentPassword) nextErrors.currentPassword = "Zadejte současné heslo.";
    if (!PASSWORD_PATTERN.test(newPassword)) {
      nextErrors.newPassword =
        "Heslo musí mít alespoň 6 znaků a obsahovat alespoň jednu číslici.";
    }
    if (!confirmPassword) {
      nextErrors.confirmPassword = "Zadejte nové heslo ještě jednou.";
    } else if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = "Hesla se neshodují.";
    }
    setErrors(nextErrors);
    const firstInvalid = PASSWORD_ERROR_ORDER.find((key) => nextErrors[key]);
    if (firstInvalid) {
      focusField(PASSWORD_SECTION_ID, PASSWORD_FIELD_IDS[firstInvalid]);
      return;
    }

    setSaving(true);
    try {
      await reauthenticate(user, currentPassword);
    } catch (err) {
      setSaving(false);
      const fieldError = passwordFieldError(err);
      if (fieldError) {
        showFieldError("currentPassword", fieldError);
      } else {
        reportError("UpdateProfile.changePassword.reauth", err, { uid: user.uid });
        showToast(firebaseErrorMessage(err, "Heslo se nepodařilo změnit."), "error");
      }
      return;
    }

    try {
      await updatePassword(user, newPassword);
    } catch (err) {
      setSaving(false);
      if (errorCode(err) === "auth/weak-password") {
        showFieldError("newPassword", firebaseErrorMessage(err));
      } else {
        reportError("UpdateProfile.changePassword", err, { uid: user.uid });
        showToast(firebaseErrorMessage(err, "Heslo se nepodařilo změnit."), "error");
      }
      return;
    }

    setSaving(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    showToast("Heslo bylo změněno.", "success");
  };

  return (
    <form className="mt-4" onSubmit={handleSubmit} noValidate>
      <Section id={PASSWORD_SECTION_ID} title="Změna hesla" icon="bi-key">
        {/* Skryté uživatelské jméno: správce hesel pozná, ke kterému účtu
            nové heslo patří (a Chrome nehlásí formulář bez username). */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={user.email ?? ""}
          readOnly
          hidden
        />

        <Field
          id={PASSWORD_FIELD_IDS.currentPassword}
          label="Současné heslo"
          required
          error={errors.currentPassword}
        >
          <input
            type="password"
            className="form-control"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              clearError("currentPassword");
            }}
          />
        </Field>

        <Field
          id={PASSWORD_FIELD_IDS.newPassword}
          label="Nové heslo"
          required
          help="Alespoň 6 znaků, z toho aspoň jedna číslice."
          error={errors.newPassword}
        >
          <input
            type="password"
            className="form-control"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              clearError("newPassword");
            }}
          />
        </Field>

        <Field
          id={PASSWORD_FIELD_IDS.confirmPassword}
          label="Nové heslo znovu"
          required
          error={errors.confirmPassword}
        >
          <input
            type="password"
            className="form-control"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              clearError("confirmPassword");
            }}
          />
        </Field>

        <div className="text-end">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            ) : (
              <i className="bi bi-key me-2" aria-hidden="true" />
            )}
            Změnit heslo
          </button>
        </div>
      </Section>
    </form>
  );
};

// ─────────────────────────────────────────────────────────────
//  3) Smazání účtu
// ─────────────────────────────────────────────────────────────

type DeleteResult =
  | { ok: true }
  | { ok: false; passwordError: string }
  | { ok: false; message: string };

/** Firestore `in` bere nejvýš 30 hodnot. */
const IN_QUERY_LIMIT = 30;

/**
 * Před smazáním účtu: odchod ze všech čekacích listin a odhlášení ze
 * NADCHÁZEJÍCÍCH závodů (datum závodu ≥ dnes). Po smazání účtu by to
 * střelec už neudělal a přihláška by dál blokovala místo v kapacitě.
 * Přihlášky ruší postupně deleteRegistrationCounted (přihláška a obsazenost
 * −1 v jedné transakci; pravidla neumí −N v jedné dávce) – BEZ náhradního
 * prostého smazání: to by obsazenost rozjelo (přihláška pryč, počítadlo
 * o 1 výš = fantomové obsazené místo, nález 34). Pak řádek veřejné
 * startovní listiny – best-effort jako odhlášení v PaymentRace. Proběhlé
 * závody i závody bez čitelného data zůstávají: přihláška je doklad platby
 * a podklad výsledků. progress.cancelled = počet zrušených přihlášek (platí
 * i po chybě). Chyba se propaguje – smazání účtu se zastaví.
 */
async function leaveUpcomingRaces(
  uid: string,
  progress: { cancelled: number }
): Promise<void> {
  // Čekací listiny nesou jen uid a čas – pryč všechny, i proběhlých závodů.
  // Před nasazením pravidel bezpečnosti (a) kolekce waitlist pravidla nemá
  // (permission-denied) – pak ještě žádná listina neexistuje.
  const waitlist = await loadMyWaitlist(uid).catch((err: unknown) => {
    if (errorCode(err) === "permission-denied") return [];
    throw err;
  });
  for (const entry of waitlist) {
    await leaveWaitlist(entry.raceId, uid);
  }

  // Ze serveru, ne z cache – smazání nesmí minout přihlášku z jiného zařízení.
  const regsSnap = await getDocsFromServer(
    query(collection(db, "registrations"), where("userId", "==", uid))
  );
  const regIdsByRace = new Map<string, string[]>();
  regsSnap.docs.forEach((d) => {
    const raceId = d.get("raceId");
    if (typeof raceId !== "string" || !raceId) return;
    const ids = regIdsByRace.get(raceId);
    if (ids) ids.push(d.id);
    else regIdsByRace.set(raceId, [d.id]);
  });
  if (regIdsByRace.size === 0) return;

  // Data závodů (veřejná) – přihláška datum nenese.
  const raceIds = Array.from(regIdsByRace.keys());
  const raceDates = new Map<string, unknown>();
  for (let i = 0; i < raceIds.length; i += IN_QUERY_LIMIT) {
    const snap = await getDocs(
      query(
        collection(db, "races"),
        where(documentId(), "in", raceIds.slice(i, i + IN_QUERY_LIMIT))
      )
    );
    snap.docs.forEach((d) => raceDates.set(d.id, d.get("date")));
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (const [raceId, regIds] of regIdsByRace) {
    const raceDay = startOfDay(raceDates.get(raceId));
    if (!raceDay || raceDay.getTime() < today.getTime()) continue;
    // I historické duplicity (víc přihlášek na jeden závod) – po jedné.
    for (const regId of regIds) {
      if (await deleteRegistrationCounted(regId)) progress.cancelled += 1;
    }
    try {
      await deleteDoc(doc(db, startListPath(raceId), uid));
    } catch (err) {
      reportWarning("UpdateProfile.deleteAccount.startList", err, { raceId });
    }
  }
}

/** Dodatek hlášky, když smazání skončilo až po odhlášení ze závodů. */
const cancelledNote = (cancelled: number): string =>
  cancelled > 0 ? " Z nadcházejících závodů jste ale už odhlášeni." : "";

/**
 * Smazání účtu v bezpečném pořadí (námitka kritika 26, bezpečnost a):
 *  1. ověření heslem – deleteUser pak nespadne na auth/requires-recent-login;
 *  2. záloha users/{uid} (+ userDocuments/{uid}) v paměti, přímo ze serveru;
 *  3. odhlášení z nadcházejících závodů a čekacích listin (leaveUpcomingRaces)
 *     – ještě s profilem: když selže, účet zůstává celý;
 *  4. smazání dokumentů (profil, doklady, rozpracovaný profil z registrace)
 *     a zápis náhrobku deletedProfiles/{uid}, dokud je uživatel přihlášený
 *     – po deleteUser je klient odhlášený, rules by deleteDoc odmítly
 *     a v DB by zůstal osiřelý profil;
 *  5. deleteUser (Firebase uživatele zároveň odhlásí);
 *  6. když deleteUser selže, vrátí se doklady; profil ne – smazání se
 *     dokončí znovu (hned, nebo po přihlášení – AuthPage ho podle náhrobku
 *     nabídne).
 * Funguje i bez profilu (záloha null – dokončení dřív nedokončeného
 * smazání, viz krok 6). Nevyhazuje – výsledek vrací jako hodnotu.
 * Export kvůli testu (UpdateProfile.deleteAccount.test.ts) a dokončení
 * smazání v AuthPage.
 */
export async function deleteAccount(user: User, password: string): Promise<DeleteResult> {
  const uid = user.uid;

  // 1) Ověření heslem
  try {
    await reauthenticate(user, password);
  } catch (err) {
    const fieldError = passwordFieldError(err);
    if (fieldError) return { ok: false, passwordError: fieldError };
    reportError("UpdateProfile.deleteAccount.reauth", err, { uid });
    return { ok: false, message: `Účet nebyl smazán. ${firebaseErrorMessage(err)}` };
  }

  const profileRef = doc(db, "users", uid);
  const documentsRef = doc(db, "userDocuments", uid);

  // 2) Záloha – ze serveru, ne z cache: obnova nesmí vrátit zastaralá data.
  //    Bez zálohy profilu se nemaže nic.
  let profileBackup: DocumentData | null;
  try {
    const snap = await getDocFromServer(profileRef);
    profileBackup = snap.exists() ? snap.data() : null;
  } catch (err) {
    reportError("UpdateProfile.deleteAccount.backupProfile", err, { uid });
    return { ok: false, message: `Účet nebyl smazán. ${firebaseErrorMessage(err)}` };
  }
  // Správce si účet sám nesmaže: kdyby deleteUser selhal, obnova profilu
  // s rolí admin/superadmin by neprošla pravidly (vlastník smí založit
  // profil jen s rolí user) a správce by přišel o profil i práva.
  const role = profileBackup ? profileBackup.role : undefined;
  if (role === "admin" || role === "superadmin") {
    return {
      ok: false,
      message:
        "Účet správce nelze smazat. Nejdřív si nechte odebrat roli administrátora.",
    };
  }
  // Doklady: null = dokument neexistuje. Bez zálohy (výpadek) se nemaže nic –
  // v tu chvíli ještě není smazáno vůbec nic.
  let documentsBackup: DocumentData | null;
  try {
    const snap = await getDocFromServer(documentsRef);
    documentsBackup = snap.exists() ? snap.data() : null;
  } catch (err) {
    reportError("UpdateProfile.deleteAccount.backupDocuments", err, { uid });
    return { ok: false, message: `Účet nebyl smazán. ${firebaseErrorMessage(err)}` };
  }

  // 3) Nadcházející závody a čekací listiny – dokud profil i účet existují.
  const progress = { cancelled: 0 };
  try {
    await leaveUpcomingRaces(uid, progress);
  } catch (err) {
    reportError("UpdateProfile.deleteAccount.leaveRaces", err, {
      uid,
      cancelled: progress.cancelled,
    });
    // Souběh s jinými přihláškami po vyčerpání opakování (RaceBusyError) –
    // stačí to zopakovat. permission-denied už souběh není (raceStats ho
    // opakuje sám), ale trvalé odmítnutí: radí se správce, ne „nemáte
    // oprávnění".
    const reason =
      err instanceof RaceBusyError
        ? err.message
        : errorCode(err) === "permission-denied"
        ? `Zkuste to prosím za chvíli znovu; pokud to nepůjde, napište správci klubu na ${CONTACT_EMAIL}.`
        : firebaseErrorMessage(err);
    return {
      ok: false,
      message:
        progress.cancelled > 0
          ? `Účet nebyl smazán. Z části nadcházejících závodů jste už odhlášeni, odhlášení ze zbylých se nepodařilo. ${reason}`
          : `Účet nebyl smazán, odhlášení z nadcházejících závodů se nepodařilo. ${reason}`,
    };
  }

  // 4) Dokumenty – ještě jako přihlášený vlastník, v JEDNÉ transakci
  //    s náhrobkem deletedProfiles/{uid}: zapíše se všechno, nebo nic.
  //    Náhrobek (nález 12 2. kola) brání vlastníkovi profil znovu založit
  //    (dřív tím obešel zákaz nováčka i zámek roku narození) a podle něj
  //    AuthPage po přihlášení nabídne dokončení smazání. Finální pravidla
  //    smazání vlastního profilu bez náhrobku v téže transakci nepustí.
  //    Zapisuje se i bez profilu (opakované smazání); existující náhrobek
  //    mění jen správce, proto se nejdřív přečte. Transakce čeká na
  //    potvrzení serveru, takže výpadek sítě uprostřed nenechá profil
  //    smazaný a účet živý (offline zařazený deleteDoc by se odeslal až při
  //    dalším spuštění).
  //    Smaže se i rozpracovaný profil z registrace (nález 2 3. kola): účet
  //    bez profilu, jehož dokončení skončilo dotazem „Jste to vy?“, by si
  //    jinak nechal jméno, rok narození a telefon i po „Účet byl smazán“
  //    a po nedokončeném smazání by z něj přihlášení zkoušelo registraci
  //    dokončit. Pravidla vlastníkovi smazání pustí, i když dokument není.
  const tombstoneRef = doc(db, DELETED_PROFILES_COLLECTION, uid);
  try {
    await runTransaction(db, async (tx) => {
      const tombstone = await tx.get(tombstoneRef);
      if (!tombstone.exists()) tx.set(tombstoneRef, { deletedAt: serverTimestamp() });
      if (profileBackup) tx.delete(profileRef);
      if (documentsBackup) tx.delete(documentsRef);
      tx.delete(doc(db, PENDING_PROFILES_COLLECTION, uid));
    });
  } catch (err) {
    reportError("UpdateProfile.deleteAccount.deleteProfile", err, { uid });
    // permission-denied = pravidla bez bloku deletedProfiles (nasazení
    // v nesprávném pořadí) – uživatel to nespraví, „nemáte oprávnění“ by mátlo.
    const reason =
      errorCode(err) === "permission-denied"
        ? `Smazání teď nejde dokončit – zkuste to prosím později; pokud to nepůjde, napište správci klubu na ${CONTACT_EMAIL}.`
        : firebaseErrorMessage(err);
    return {
      ok: false,
      message: `Účet nebyl smazán.${cancelledNote(progress.cancelled)} ${reason}`,
    };
  }

  // 5) Účet ve Firebase Auth
  try {
    await deleteUser(user);
    return { ok: true };
  } catch (err) {
    reportError("UpdateProfile.deleteAccount.deleteUser", err, { uid });

    // 6) Přihlašovací účet zůstal. Profil se neobnovuje: náhrobek nové
    //    založení vlastníkem nepustí (finální pravidla) a smazání se má
    //    dokončit – hned znovu (stránka i sekce Smazání účtu fungují bez
    //    profilu: záloha null, kroky 3–4 jen to, co existuje, pak
    //    deleteUser), nebo po příštím přihlášení, kde ho AuthPage podle
    //    náhrobku nabídne (utils/completeRegistration – lks/account-deleted).
    //    Doklady vlastník obnoví; dokončení smazání je smaže znovu.
    if (documentsBackup) {
      try {
        await setDoc(documentsRef, documentsBackup);
      } catch (restoreErr) {
        reportWarning("UpdateProfile.deleteAccount.restoreDocuments", restoreErr, {
          uid,
        });
      }
    }
    return {
      ok: false,
      message: `Údaje profilu jsou smazané, přihlašovací účet se ale smazat nepodařilo. ${firebaseErrorMessage(err)} Smazání zkuste dokončit znovu – hned, nebo po příštím přihlášení vám ho aplikace nabídne. Pokud to nepůjde, napište správci klubu na ${CONTACT_EMAIL}.`,
    };
  }
}

/**
 * Potvrzení „Účet byl smazán“ pro přihlašovací stránku (sessionStorage).
 * Po smazání se kvůli vymazání cache stránka načte znovu (restartApp), takže
 * toast by zmizel. AuthPage potvrzení ukáže – i po novém načtení – a zapomene
 * ho při další akci (přihlášení, registrace…).
 */
const ACCOUNT_DELETED_KEY = "lks-account-deleted";

export function rememberAccountDeleted(): void {
  try {
    window.sessionStorage.setItem(ACCOUNT_DELETED_KEY, new Date().toISOString());
  } catch {
    // Zakázané úložiště – stránka se načte bez potvrzení (smazání proběhlo).
  }
}

export function accountDeletedRemembered(): boolean {
  try {
    return window.sessionStorage.getItem(ACCOUNT_DELETED_KEY) !== null;
  } catch {
    return false;
  }
}

export function forgetAccountDeleted(): void {
  try {
    window.sessionStorage.removeItem(ACCOUNT_DELETED_KEY);
  } catch {
    // Zakázané úložiště – není co zapomenout.
  }
}

/** Sekce se smazáním účtu a potvrzovací dialog (heslo + opsání slova SMAZAT). */
const DeleteAccountSection: React.FC<{ user: User }> = ({ user }) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Pojistka proti dvojímu spuštění nezávislá na tom, kdy se překreslí stav.
  const busyRef = useRef(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const confirmOk =
    confirmText.trim().toUpperCase() === DELETE_CONFIRM_WORD.toUpperCase();
  const canSubmit = password !== "" && confirmOk && !deleting;

  const resetDialog = () => {
    setPassword("");
    setConfirmText("");
    setPasswordError(undefined);
    setError(null);
  };

  const openDialog = () => {
    resetDialog();
    setOpen(true);
  };

  // Během mazání dialog nejde zavřít (Esc, pozadí, ×) – tok musí doběhnout.
  const closeDialog = () => {
    if (busyRef.current) return;
    setOpen(false);
    resetDialog();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busyRef.current || !canSubmit) return;
    busyRef.current = true;
    setDeleting(true);
    setPasswordError(undefined);
    setError(null);

    const result = await deleteAccount(user, password);
    if (result.ok) {
      // Jako odhlášení (UserProfilePage): v cache Firestore by na zařízení
      // zůstal profil, číslo dokladu i přihlášky (nález 13 2. kola) – vymazat
      // a stránku načíst znovu. Potvrzení ukáže přihlašovací stránka
      // (sessionStorage – toast by novým načtením zmizel). Dialog zůstává
      // zamčený (busyRef), stránka se stejně načte znovu.
      rememberAccountDeleted();
      navigate("/login", { replace: true });
      await clearFirestoreCache();
      restartApp("/login");
      return;
    }

    busyRef.current = false;
    setDeleting(false);
    if ("passwordError" in result) {
      // Špatné heslo: chyba u pole, dialog zůstává otevřený.
      setPasswordError(result.passwordError);
      if (passwordRef.current) {
        passwordRef.current.focus();
        passwordRef.current.select();
      }
    } else {
      setError(result.message);
    }
  };

  const dialog = (
    <Modal
      open={open}
      onClose={closeDialog}
      title="Smazat účet?"
      initialFocusRef={cancelRef}
      footer={
        <div className="lks-modal__actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeDialog}
            disabled={deleting}
          >
            Zrušit
          </button>
          <button
            type="submit"
            form={DELETE_FORM_ID}
            className="btn btn-danger"
            disabled={!canSubmit}
          >
            {deleting ? (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            ) : (
              <i className="bi bi-trash3 me-2" aria-hidden="true" />
            )}
            Smazat účet
          </button>
        </div>
      }
    >
      <div className="lks-confirm__message">
        Účet a údaje profilu se smažou natrvalo. Akci nelze vrátit.
      </div>
      <div className="lks-confirm__details">
        <ul className="mb-0 ps-3">
          <li>Do aplikace se už nepřihlásíte.</li>
          <li>
            Smaže se profil (jméno, telefon, zbrojní průkaz, členství)
            i uložené číslo dokladu.
          </li>
          <li>
            Přihlášky na nadcházející závody se zruší a uvolní se místo.
            O vrácení už zaplaceného startovného napište na{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </li>
          <li>Přihlášky a výsledky z proběhlých závodů zůstanou zachované.</li>
        </ul>
      </div>

      <form
        id={DELETE_FORM_ID}
        className="mt-3"
        onSubmit={handleSubmit}
        noValidate
      >
        <Field
          id="deletePassword"
          label="Heslo"
          required
          help="Smazání potvrďte heslem, kterým se přihlašujete."
          error={passwordError}
        >
          <input
            ref={passwordRef}
            type="password"
            className="form-control"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError(undefined);
            }}
          />
        </Field>

        <Field
          id="deleteConfirm"
          label={`Pro potvrzení opište slovo ${DELETE_CONFIRM_WORD}`}
          required
          className="mb-0"
        >
          <input
            type="text"
            className="form-control"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </Field>

        {error ? (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </Modal>
  );

  return (
    <>
      <Section title="Smazání účtu" icon="bi-trash3">
        <p className="mb-3">
          Smaže přihlašovací účet i údaje profilu a zruší přihlášky na
          nadcházející závody. Akci nelze vrátit.
        </p>
        <button
          type="button"
          className="btn btn-outline-danger"
          aria-haspopup="dialog"
          onClick={openDialog}
        >
          <i className="bi bi-trash3 me-2" aria-hidden="true" />
          Smazat účet
        </button>
      </Section>
      {dialog}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
//  Stránka
// ─────────────────────────────────────────────────────────────

/** Ruční nové načtení profilu, když se napoprvé nenačetl (výpadek sítě). */
type ProfileReload =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: DocumentData }
  | { status: "missing" }
  | { status: "error"; error: unknown };

const UpdateProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, userDoc, loading, missing } = useCurrentUser();
  const [reload, setReload] = useState<ProfileReload>({ status: "idle" });
  const passwordDirtyRef = useRef(false);
  const handlePasswordDirty = useCallback((dirty: boolean) => {
    passwordDirtyRef.current = dirty;
  }, []);

  const profileDoc = userDoc ?? (reload.status === "ready" ? reload.data : null);

  const retryProfile = async () => {
    if (!user) return;
    setReload({ status: "loading" });
    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      setReload(
        snap.exists()
          ? { status: "ready", data: snap.data() }
          : { status: "missing" }
      );
    } catch (err) {
      reportError("UpdateProfile.reloadProfile", err, { uid: user.uid });
      setReload({ status: "error", error: err });
    }
  };

  // Nepřihlášeného přesměruje RequireAuth – do té doby jen spinner.
  if (loading || !user) return <LoadingState text="Načítám profil…" />;

  let profileContent: React.ReactNode;
  if (profileDoc) {
    profileContent = (
      <ProfileForm
        key={user.uid}
        user={user}
        userDoc={profileDoc}
        passwordDirty={passwordDirtyRef}
      />
    );
  } else if (reload.status === "loading") {
    profileContent = (
      <Section title="Osobní údaje" icon="bi-person">
        <LoadingState text="Načítám profil…" />
      </Section>
    );
  } else if (
    reload.status === "missing" ||
    (reload.status === "idle" && missing)
  ) {
    profileContent = (
      <Section title="Osobní údaje" icon="bi-person">
        {/* Např. nedokončená registrace nebo nedokončené smazání účtu
            (deleteAccount, krok 6) – přihlašovací stránka (AuthPage) podle
            stavu nabídne dotaz „Jste to vy?“, doplnění údajů, resp.
            dokončení smazání. Stejný text jako Profil, Členství a Přihláška. */}
        <EmptyState
          icon="bi-person-x"
          title="Profil nenalezen"
          text={`K účtu chybí údaje člena – registrace se nedokončila. Dokončete ji tlačítkem níže (je k tomu potřeba připojení k internetu). Pokud jste účet mazali, dokončíte tam i smazání. Když to nepůjde, napište nám na ${CONTACT_EMAIL}.`}
          action={{ label: "Dokončit registraci", onClick: () => navigate("/login") }}
        />
      </Section>
    );
  } else {
    // Změna hesla a smazání účtu fungují i bez profilu – zůstávají dostupné.
    profileContent = (
      <Section title="Osobní údaje" icon="bi-person">
        <ErrorState
          error={reload.status === "error" ? reload.error : undefined}
          onRetry={retryProfile}
        />
      </Section>
    );
  }

  return (
    <>
      {profileContent}
      <PasswordForm user={user} onDirtyChange={handlePasswordDirty} />
      <DeleteAccountSection user={user} />
    </>
  );
};

export default UpdateProfile;
